import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

type GuardStatus = {
  is_active: boolean;
  message: string;
  start_time: string | null;
  end_time: string | null;
  bypass_token: string;
};

type GuardState = {
  loading: boolean;
  active: boolean;
  message: string;
  startTime: string | null;
  endTime: string | null;
  showWarning: boolean;
};

type Props = {
  appSlug: string;
  logo: string;
  serviceUrl: string;
  appName: string;
  children: ReactNode;
};

/* ─── guard ─────────────────────────────────────────────── */

export function MaintenanceGuard({
  appSlug,
  logo,
  serviceUrl,
  appName,
  children,
}: Props) {
  const [state, setState] = useState<GuardState>({
    loading: true,
    active: false,
    message: '',
    startTime: null,
    endTime: null,
    showWarning: false,
  });

  const storageKey = useMemo(() => `bypass_${appSlug}`, [appSlug]);
  const warningDismissKey = useMemo(
    () => `warning_dismissed_${appSlug}`,
    [appSlug],
  );

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2000);

    async function run() {
      try {
        const res = await fetch(`${serviceUrl}/api/v1/status/${appSlug}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`status fetch failed with ${res.status}`);
        const data = (await res.json()) as GuardStatus;

        const urlToken = new URLSearchParams(window.location.search).get(
          'maint_token',
        );
        if (urlToken) localStorage.setItem(storageKey, urlToken);

        const localToken = localStorage.getItem(storageKey);
        const isBypassed =
          localToken !== null && localToken === data.bypass_token;
        const startsAt = data.start_time
          ? new Date(data.start_time).getTime()
          : Number.NaN;
        const isUpcoming = !Number.isNaN(startsAt) && startsAt > Date.now();
        const dismissedST = sessionStorage.getItem(warningDismissKey);
        const showWarning =
          !data.is_active &&
          !isBypassed &&
          isUpcoming &&
          dismissedST !== data.start_time;

        if (mounted)
          setState({
            loading: false,
            active: data.is_active && !isBypassed,
            message: data.message,
            startTime: data.start_time,
            endTime: data.end_time,
            showWarning,
          });
      } catch {
        if (mounted)
          setState({
            loading: false,
            active: false,
            message: '',
            startTime: null,
            endTime: null,
            showWarning: false,
          });
      } finally {
        window.clearTimeout(timer);
      }
    }

    run();
    return () => {
      mounted = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [appSlug, serviceUrl, storageKey]);

  useEffect(() => {
    const targets: number[] = [];
    const now = Date.now();
    if (state.startTime) {
      const s = new Date(state.startTime).getTime();
      if (!Number.isNaN(s) && s > now) targets.push(s);
    }
    if (state.endTime) {
      const e = new Date(state.endTime).getTime();
      if (!Number.isNaN(e) && e > now) targets.push(e);
    }
    if (targets.length === 0) return;
    const id = window.setInterval(() => {
      if (targets.some((t) => Date.now() >= t)) window.location.reload();
    }, 1000);
    return () => window.clearInterval(id);
  }, [state.startTime, state.endTime, state.active]);

  useEffect(() => {
  if (!state.startTime || state.active) return;

  const startMs = new Date(state.startTime).getTime();
  if (Number.isNaN(startMs)) return;

  const fiveMinBefore = startMs - 5 * 60 * 1000;
  const now = Date.now();

  if (now >= fiveMinBefore && now < startMs) {
    setState((prev) => ({ ...prev, showWarning: true }));
    return;
  }

  if (now < fiveMinBefore) {
    const id = window.setTimeout(() => {
      setState((prev) => ({ ...prev, showWarning: true }));
    }, fiveMinBefore - now);

    return () => window.clearTimeout(id);
  }
}, [state.startTime, state.active]);

  if (state.loading) return null;

  if (!state.active) {
    return (
      <>
        {children}
        {state.showWarning && (
          <MaintenanceWarningModal
            appName={appName}
            message={state.message}
            startTime={state.startTime}
            endTime={state.endTime}
            onDismiss={() => {
              // if (state.startTime) {
              //   sessionStorage.setItem(warningDismissKey, state.startTime);
              // }
              setState((prev) => ({ ...prev, showWarning: false }));
            }}
          />
        )}
      </>
    );
  }

  return (
    <MaintenanceScreen
      logo={logo}
      message={state.message}
      endTime={state.endTime}
      appName={appName}
    />
  );
}

/* ─── helpers ────────────────────────────────────────────────────────────── */

function toLocalDateTime(v: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function useCountdown(target: string | null): string {
  const [label, setLabel] = useState('');
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = new Date(target).getTime() - Date.now();
      if (!diff || diff <= 0) {
        setLabel('En breve');
        return;
      }
      const s = Math.floor(diff / 1000);
      const d = Math.floor(s / 86400),
        h = Math.floor((s % 86400) / 3600);
      const m = Math.floor((s % 3600) / 60),
        sec = s % 60;
      setLabel(d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${sec}s`);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [target]);
  return label || 'Calculando...';
}

/* ─── pantalla mantenimiento activo ──────────────────────────────────────── */

function MaintenanceScreen({
  logo,
  message,
  endTime,
  appName,
}: {
  logo: string;
  message: string;
  endTime: string | null;
  appName: string;
}) {
  const countdown = useCountdown(endTime);
  const localEnd = useMemo(() => toLocalDateTime(endTime), [endTime]);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 40);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{globalCss}</style>
      <section style={{ ...s.wrapper, opacity: show ? 1 : 0 }}>
        <div aria-hidden style={s.glow} />
        <article style={s.card}>
          <div className="_a1" style={s.appHeader}>
            <div style={s.logoWrap}>
              <img src={logo} alt="" style={s.logoImg} />
            </div>
            <span style={s.appName}>{appName}</span>
          </div>
          <div style={s.divider} />
          <div className="_a2">
            <Badge active />
          </div>
          <h1 className="_a3" style={s.title}>
            {message || 'Estamos actualizando la plataforma.'}
          </h1>
          <p style={{
    fontSize: '.75rem', marginBottom: "-1rem"
          }}>El tiempo restanto pude ser mayor por imprevistos</p>
          <div
            className="_a4"
            style={{
              ...s.tileGrid,
              gridTemplateColumns: endTime ? '1fr 1fr' : '1fr',
            }}
          >
            <Tile
              label="Tiempo restante"
              value={endTime ? countdown : '—'}
              accent
            />
            {endTime && <Tile label="Hora prevista" value={localEnd} />}
          </div>
          <p className="_a5" style={s.footer}>
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Sistema protegido · Mantenimiento programado
          </p>
        </article>
      </section>
    </>
  );
}

/* ─── modal aviso programado ─────────────────────────────────────────────── */

type WarningModalProps = {
  appName: string;
  message: string;
  startTime: string | null;
  endTime: string | null;
  onDismiss: () => void;
};

function MaintenanceWarningModal({
  appName,
  message,
  startTime,
  endTime,
  onDismiss,
}: WarningModalProps) {
  const countdown = useCountdown(startTime);
  const localStart = useMemo(() => toLocalDateTime(startTime), [startTime]);
  const localEnd = useMemo(() => toLocalDateTime(endTime), [endTime]);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 30);
    return () => clearTimeout(t);
  }, []);

  

  return (
    <>
      <style>{globalCss}</style>
      <div role="presentation" style={{ ...s.backdrop, opacity: show ? 1 : 0 }}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Aviso de mantenimiento programado"
          style={s.modal}
        >
          <Badge active={false} />
          <div>
            <h2 style={s.modalTitle}>{appName}</h2>
            <p style={s.modalMessage}>
              {message ||
                'Hay una ventana de mantenimiento programada para esta aplicación.'}
            </p>
          </div>
          <div style={s.divider} />
          <div style={s.modalTiles}>
            <Tile label="Comienza en" value={countdown} accent large />
            <div style={s.modalTilesBottom}>
              <Tile label="Inicio" value={localStart || 'Pendiente'} />
              <Tile label="Fin" value={localEnd || 'Pendiente'} />
            </div>
          </div>
          <div style={s.modalActions}>
            <button
              type="button"
              onClick={onDismiss}
              className="_btn"
              style={s.btn}
            >
              Entendido, continuar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── componentes compartidos ───────────────────────────────────────────── */

function Badge({ active }: { active: boolean }) {
  return (
    <span style={active ? s.badgeActive : s.badgeScheduled}>
      <span style={active ? s.dotActive : s.dotScheduled} />
      {active ? 'Mantenimiento activo' : 'Programado'}
    </span>
  );
}

function Tile({
  label,
  value,
  accent,
  large,
}: {
  label: string;
  value: string;
  accent?: boolean;
  large?: boolean;
}) {
  return (
    <div
      className={accent ? '_tile _tileAccent' : '_tile'}
      style={
        accent
          ? {
              ...s.tile,
              ...s.tileAccent,
              padding: large ? '1rem 1.2rem' : '.9rem 1rem',
            }
          : { ...s.tile, padding: large ? '1rem 1.2rem' : '.9rem 1rem' }
      }
    >
      <p
        style={accent ? { ...s.tileLabel, ...s.tileLabelAccent } : s.tileLabel}
      >
        {label}
      </p>
      <p
        style={
          accent
            ? {
                ...s.tileValue,
                ...s.tileValueAccent,
                fontSize: large ? '1.4rem' : '1rem',
              }
            : { ...s.tileValue, fontSize: large ? '1.4rem' : '1rem' }
        }
      >
        {value}
      </p>
    </div>
  );
}

/* ─── css global (animaciones y clases hover) ────────────────────────────── */

const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
  @keyframes _up    { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
  @keyframes _in    { from { opacity:0; transform:scale(.97) } to { opacity:1; transform:scale(1) } }
  @keyframes _pulse { 0%,100% { box-shadow:0 0 0 0 rgba(45,78,245,.4) } 60% { box-shadow:0 0 0 5px rgba(45,78,245,0) } }
  ._a1 { animation: _up .45s cubic-bezier(.22,1,.36,1) .05s both }
  ._a2 { animation: _up .45s cubic-bezier(.22,1,.36,1) .12s both }
  ._a3 { animation: _up .45s cubic-bezier(.22,1,.36,1) .20s both }
  ._a4 { animation: _up .45s cubic-bezier(.22,1,.36,1) .29s both }
  ._a5 { animation: _up .45s cubic-bezier(.22,1,.36,1) .38s both }
  ._tile          { transition: background .12s }
  ._tile:hover    { background: #e8eaf0 !important }
  ._tileAccent:hover { background: #dde5ff !important }
  ._btn           { transition: background .12s, color .12s, border-color .12s }
  ._btn:hover     { background: #2d4ef5 !important; color: #fff !important; border-color: #2d4ef5 !important }
`;

/* ─── estilos ────────────────────────────────────────────────────────────── */

const s: Record<string, CSSProperties> = {
  // layout pantalla activa
  wrapper: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: '2rem 1.25rem',
    fontFamily: '"DM Sans", system-ui, sans-serif',
    background: '#f6f7f9',
    position: 'relative',
    overflow: 'hidden',
    transition: 'opacity .2s',
  },
  glow: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    background:
      'radial-gradient(ellipse 70% 50% at 100% 0%, rgba(45,78,245,.07) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    width: 'min(580px, 100%)',
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e4ec',
    boxShadow: '0 1px 3px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.05)',
    padding: 'clamp(1.6rem, 4vw, 2.4rem)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.2rem',
  },

  // app header
  appHeader: { display: 'flex', alignItems: 'center', gap: '.65rem' },
  logoWrap: {
    width: '36px',
    height: '36px',
    borderRadius: '9px',
    flexShrink: 0,
    border: '1px solid #e2e4ec',
    background: '#f0f1f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logoImg: { width: '24px', height: '24px', objectFit: 'contain' },
  appName: { fontSize: '.82rem', fontWeight: 600, color: '#5a5f7a' },

  // divisor
  divider: { height: '1px', background: '#e2e4ec' },

  // título
  title: {
    fontSize: 'clamp(1.6rem, 4vw, 2.3rem)',
    fontWeight: 700,
    lineHeight: 1.12,
    letterSpacing: '-.025em',
    color: '#0f1117',
    margin: 0,
  },

  // tiles grid
  tileGrid: { display: 'grid', gap: '.55rem' },

  // tile base
  tile: {
    borderRadius: '10px',
    border: '1px solid #e2e4ec',
    background: '#f0f1f5',
  },
  tileAccent: {
    border: '1px solid #93a8f4',
    background: '#f0f4ff',
  },
  tileLabel: {
    fontSize: '.62rem',
    fontWeight: 600,
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    color: '#9ea4be',
    margin: '.3rem auto',
  },
  tileLabelAccent: { color: '#2d4ef5' },
  tileValue: {
    fontWeight: 700,
    color: '#0f1117',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
    margin: 0,
  },
  tileValueAccent: { color: '#2d4ef5' },

  // footer
  footer: {
    fontSize: '.72rem',
    color: '#9ea4be',
    display: 'flex',
    alignItems: 'center',
    gap: '.35rem',
    margin: 0,
  },

  // badge activo
  badgeActive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.4rem',
    padding: '.25rem .75rem .25rem .5rem',
    borderRadius: '999px',
    fontSize: '.67rem',
    fontWeight: 700,
    letterSpacing: '.07em',
    textTransform: 'uppercase',
    background: '#fff0f0',
    border: '1px solid #fca5a5',
    color: '#b91c1c',
  },
  badgeScheduled: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.4rem',
    padding: '.25rem .75rem .25rem .5rem',
    borderRadius: '999px',
    fontSize: '.67rem',
    fontWeight: 700,
    letterSpacing: '.07em',
    textTransform: 'uppercase',
    background: '#e8edff',
    border: '1px solid #93a8f4',
    color: '#2d4ef5',
  },
  dotActive: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#ef4444',
    animation: '_pulse 2s ease infinite',
    flexShrink: 0,
  },
  dotScheduled: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#2d4ef5',
    animation: '_pulse 2s ease infinite',
    flexShrink: 0,
  },

  // modal backdrop
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 999,
    background: 'rgba(15,17,23,.08)',
    backdropFilter: 'blur(5px) saturate(1.4)',
    display: 'grid',
    placeItems: 'center',
    padding: '1rem',
    fontFamily: '"DM Sans", system-ui, sans-serif',
    transition: 'opacity .18s',
  },

  // modal
  modal: {
    width: 'min(480px, 100%)',
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e4ec',
    boxShadow: '0 8px 40px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06)',
    padding: '1.8rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    animation: '_in .28s cubic-bezier(.22,1,.36,1) both',
  },
  modalTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    letterSpacing: '-.02em',
    color: '#0f1117',
    lineHeight: 1.2,
    margin: '0 0 .4rem',
  },
  modalMessage: {
    fontSize: '.88rem',
    color: '#5a5f7a',
    lineHeight: 1.65,
    margin: 0,
  },
  modalTiles: { display: 'flex', flexDirection: 'column', gap: '.5rem' },
  modalTilesBottom: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '.5rem',
  },
  modalActions: { display: 'flex', justifyContent: 'flex-end' },

  // botón
  btn: {
    padding: '.5rem 1.2rem',
    borderRadius: '8px',
    border: '1px solid #93a8f4',
    background: '#f0f4ff',
    color: '#2d4ef5',
    fontSize: '.84rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: '"DM Sans", system-ui, sans-serif',
  },
};
